/**
 * US A4 — quick-create a Plot with server-side GPS duplicate check (POSSIBLE_DUPLICATE,
 * 409). Per ADR-6 (farmer-offline-sync skill §5): plot creation always requires network —
 * the form is disabled outright when offline instead of silently queuing (queuing would
 * risk two enumerators creating the same physical plot twice, which is exactly what the
 * duplicate check exists to prevent).
 */
import * as api from '../api-client.js';
import { toast, navigate, buildHash, parseHash, esc } from '../ui/util.js';
import { gpsFieldHtml, attachGpsCapture, readGpsPair } from '../ui/gps-capture.js';
import {
  soilTypeSelectHtml,
  attachSoilTypeHint,
  isSoilType,
  SOIL_TYPE_REQUIRED_MESSAGE,
} from '../ui/soil-type.js';

/**
 * Địa bàn của Vườn hiển thị chỉ để đối chiếu, không cho sửa tại đây.
 *
 * Bảng `plots` không có cột địa bàn: Vườn thuộc về một Hộ và thừa hưởng địa bàn
 * của Hộ đó — đúng với thực tế địa chỉ Hộ và Vườn trùng nhau. Đặt một ô chọn
 * Tỉnh/Xã riêng ở đây sẽ ngụ ý sai rằng Vườn có địa bàn độc lập với Hộ, và mọi
 * thay đổi thực chất lại ghi sang hồ sơ Hộ. Muốn sửa thì sửa ở hồ sơ Hộ.
 */
function renderRegionRow(household, householdId) {
  const region = household.region;
  const text = region ? (region.parent ? `${region.name}, ${region.parent.name}` : region.name) : null;
  const editLink = `<a class="btn btn-text sm" href="${buildHash(`/households/${householdId}`)}">Sửa ở hồ sơ hộ</a>`;

  if (!text) {
    return `<div class="status-banner tone-error">
      <span class="ms">wrong_location</span>
      <span>Hộ này chưa có địa bàn — Vườn tạo ra sẽ nằm ngoài phạm vi của điều tra viên và có thể không tìm thấy lại được.</span>
      ${editLink}</div>`;
  }
  return `<div class="status-banner tone-info">
    <span class="ms">location_on</span>
    <span>Địa bàn: <b>${esc(text)}</b> — Vườn dùng chung địa bàn với Hộ.</span>
    ${editLink}</div>`;
}

export async function renderPlotCreate(container) {
  const { params } = parseHash();
  const householdId = params.householdId;
  const returnTo = params.returnTo || null;

  if (!householdId) {
    container.innerHTML = `<div class="empty-state"><p>Cần chọn một Hộ trước khi tạo Vườn.</p>
      <a class="btn" href="${buildHash('/households', { pick: '1', returnTo: '/plots/new' })}">Chọn hộ</a></div>`;
    return;
  }

  container.innerHTML = `<div class="empty-state">Đang tải thông tin hộ...</div>`;
  let household;
  try {
    household = await api.households.get(householdId);
  } catch (err) {
    container.innerHTML = `<div class="empty-state">${esc(err.message)}</div>`;
    return;
  }

  container.innerHTML = `
    <div class="page-head"><div><h1>Thêm Vườn mới</h1><p>Cho hộ: <b>${esc(household.fullName)}</b></p></div></div>
    ${!navigator.onLine ? `<div class="status-banner tone-error"><span class="ms">cloud_off</span>
      <span>Cần có mạng để tạo hồ sơ mới. Bạn vẫn có thể tiếp tục nhập phiếu cho hộ đã chọn.</span></div>` : ''}
    ${renderRegionRow(household, householdId)}
    <div class="card">
      <div id="dupWarning"></div>
      <form id="plotForm">
        <div class="form-grid">
          <div class="form-row"><label>Diện tích canh tác (ha) *</label><input name="areaHa" type="number" step="0.01" min="0.01" required></div>
          ${soilTypeSelectHtml()}
          ${gpsFieldHtml()}
          <div class="form-row"><label>Mã vườn (tuỳ chọn, tự sinh nếu để trống)</label><input name="plotCode"></div>
        </div>
        <div class="error-text" id="plotError" hidden></div>
        <button class="btn" type="submit" id="plotSubmit" ${navigator.onLine ? '' : 'disabled'}>Lưu vườn mới</button>
      </form>
    </div>`;

  const form = container.querySelector('#plotForm');
  attachGpsCapture(form);
  attachSoilTypeHint(form);
  const errBox = container.querySelector('#plotError');
  const dupBox = container.querySelector('#dupWarning');
  const submitBtn = container.querySelector('#plotSubmit');

  async function submit(payload) {
    submitBtn.disabled = true;
    try {
      const plot = await api.plots.create(payload);
      toast('Đã tạo vườn mới');
      finish(plot);
    } catch (err) {
      if (err.code === 'POSSIBLE_DUPLICATE') {
        renderDuplicateWarning(err, payload);
      } else {
        errBox.textContent = err.message;
        errBox.hidden = false;
      }
    } finally {
      submitBtn.disabled = false;
    }
  }

  function renderDuplicateWarning(err, payload) {
    dupBox.innerHTML = `<div class="gps-warning">
      <strong><span class="ms ms-inline">warning</span> ${esc(err.message)}</strong>
      <ul>${(err.candidates || []).map((c) => `<li>${esc(c.plotCode || c.id)} — ${esc(c.householdName)} — cách ${c.distanceM}m
        <button class="btn btn-text sm" data-use="${esc(c.id)}">Dùng vườn này</button></li>`).join('')}</ul>
      <button class="btn btn-tonal sm" id="forceCreateBtn">Vẫn tạo vườn mới (đã xác nhận đây là vườn khác)</button>
    </div>`;
    dupBox.querySelectorAll('[data-use]').forEach((btn) => {
      btn.addEventListener('click', () => finish({ id: btn.dataset.use }));
    });
    dupBox.querySelector('#forceCreateBtn').addEventListener('click', () => submit({ ...payload, forceCreate: true }));
  }

  function finish(plot) {
    if (returnTo) navigate(buildHash(returnTo, { householdId, plotId: plot.id }));
    else navigate(buildHash(`/households/${householdId}`));
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    errBox.hidden = true;
    dupBox.innerHTML = '';
    const fd = new FormData(form);
    const soilType = fd.get('soilType');
    // `soilType` là bắt buộc từ 2026-08-23 và `CreatePlotDto` chặn bằng `@IsIn`. Kiểm ở
    // đây để lỗi hiện ngay dưới form thay vì quay về một 422 chung từ máy chủ.
    if (!isSoilType(soilType)) {
      errBox.textContent = SOIL_TYPE_REQUIRED_MESSAGE;
      errBox.hidden = false;
      return;
    }
    const gps = readGpsPair(fd.get('gpsLat'), fd.get('gpsLng'));
    if (gps.error) {
      errBox.textContent = gps.error;
      errBox.hidden = false;
      return;
    }
    const payload = {
      householdId,
      areaHa: Number(fd.get('areaHa')),
      gpsLat: gps.gpsLat,
      gpsLng: gps.gpsLng,
      soilType,
      plotCode: fd.get('plotCode').trim() || undefined,
    };
    submit(payload);
  });
}
